// src/components/atomic-crm/contacts/ContactListContent.tsx
import { formatRelative } from "date-fns";
import { RecordContextProvider, useListContext, useTranslate } from "ra-core";
import { type MouseEvent, useCallback } from "react";
import { Link } from "react-router";
import { ReferenceField } from "@/components/ds/admin/reference-field";
import { TextField } from "@/components/ds/admin/text-field";
import { Checkbox } from "@/components/ds/ui/checkbox";
import { Skeleton } from "@/components/ds/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { Status } from "../misc/Status";
import type { Contact } from "../types";
import { Avatar } from "./Avatar";
import { TagsList } from "./TagsList";

export const ContactListContent = () => {
  const {
    data: contacts,
    error,
    isPending,
    onToggleItem,
    selectedIds,
  } = useListContext<Contact>();
  const isSmall = useIsMobile();
  const translate = useTranslate();

  const handleLinkClick = useCallback(function handleLinkClick(
    e: MouseEvent<HTMLAnchorElement>,
  ) {
    if (e.target instanceof HTMLButtonElement) {
      e.preventDefault();
    }
  }, []);

  if (isPending) {
    return <Skeleton className="w-full h-9" />;
  }
  if (error) return null;

  const now = Date.now();

  return (
    <div className="divide-y divide-border">
      {contacts.map((contact) => (
        <RecordContextProvider key={contact.id} value={contact}>
          <Link
            to={`/contacts/${contact.id}/show`}
            className={cn(
              "group flex gap-3 px-4 py-3 transition-colors",
              "hover:bg-muted/50",
              // Desktop: single row; Mobile: stacked
              "flex-row items-center",
              "md:items-center",
            )}
            onClick={handleLinkClick}
          >
            <Checkbox
              className="cursor-pointer shrink-0"
              checked={selectedIds.includes(contact.id)}
              onCheckedChange={() => onToggleItem(contact.id)}
            />

            <Avatar />

            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">
                {`${contact.first_name} ${contact.last_name ?? ""}`}
              </div>
              <div className="text-sm text-muted-foreground truncate">
                {contact.title}
                {contact.title &&
                  contact.company_id != null &&
                  ` ${translate("crm.contact.field.at")} `}
                {contact.company_id != null && (
                  <ReferenceField
                    source="company_id"
                    reference="companies"
                    link={false}
                  >
                    <TextField source="name" />
                  </ReferenceField>
                )}
              </div>
              {/* Mobile-only meta line: task count + tags + last seen */}
              <div className="md:hidden text-xs text-muted-foreground flex items-center gap-2 mt-1">
                {contact.last_seen && (
                  <span title={contact.last_seen}>
                    {formatRelative(contact.last_seen, now)}
                  </span>
                )}
                <Status status={contact.status} />
              </div>
            </div>

            {/* Desktop-only right column: last seen + status */}
            {contact.last_seen && (
              <div className="hidden md:block text-right ml-4 shrink-0">
                <div
                  className="text-sm text-muted-foreground"
                  title={contact.last_seen}
                >
                  {!isSmall &&
                    `${translate("resources.contacts.fields.last_seen").toLowerCase()} `}
                  {formatRelative(contact.last_seen, now)}{" "}
                  <Status status={contact.status} />
                </div>
              </div>
            )}
          </Link>
        </RecordContextProvider>
      ))}

      {contacts.length === 0 && (
        <div className="p-4">
          <div className="text-muted-foreground">
            {translate("crm.contact.empty.title")}
          </div>
        </div>
      )}
    </div>
  );
};
